import { ThemeKey } from "@smartsheet/design-tokens";
import { genId } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import React, { forwardRef, useRef, useState, useMemo, ReactNode, ReactChild, HTMLAttributes } from "react";
import {
    I18NContextConsumer,
    I18nContextProvider,
    I18nProviderPropsStrict,
    WithI18nInjectedProps,
} from "../../../i18n/i18n";
import { useTheme } from "../../../theme/utils/useTheme";
import { InputFieldBaseProps } from "../../../util/inputFieldBaseProps";
import { FlyoutProps } from "../../flyout";
import { FormLabel } from "../../formlabel";
import { TextInputProps } from "../../textinput";
import { TypeRampV2Props } from "../../typography";
import { DatePickerDialog } from "../dialog/components/DatePickerDialog";
import { useDatePickerDialog } from "../dialog/hooks/useDatePickerDialog";
import { datePickerTheme } from "../dialog/styled/datePickerTheme";
import { injectCatalog } from "../i18n/DatePickerCatalogs";
import { DatePickerFormat, DatePickerDate, DatePickerComponentOverrideProps } from "../types";
import { AtomicDatePickerInput } from "./components/AtomicDatePickerInput";
import { dateToInputString, DatePickerInputValidationResult, ValidationMessages } from "./utils";

export interface DatePickerInputPropsBase
    extends I18nProviderPropsStrict,
        InputFieldBaseProps,
        Omit<
            React.InputHTMLAttributes<HTMLInputElement>,
            "defaultValue" | "disabled" | "onChange" | "readOnly" | "style" | "type" | "value"
        > {
    /**
     * The Date format displayed to the user in the input.
     */
    format: DatePickerFormat;

    /**
     * Callback for date change.
     * Will be called when a date valid for the given format is either
     * typed in or selected via the dialog.
     *
     * Can be called with null if the user deletes all content.
     */
    onChange: (date: DatePickerDate | null) => void;

    /**
     * The current date, if there is one. This is assumed to be in UTC and all date
     * manipulations will assume UTC. It should not have any time associated with
     * it i.e. it's always midnight in DatePicker land.
     */
    value: DatePickerDate | null;

    /**
     * A classname to be applied to the root element of this component.
     */
    className?: string;

    /**
     * A `clientId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests
     */
    clientId?: string;

    /**
     * If provided, called for each day in the current calendar viewport to check
     * if it should be selectable or not.
     *
     * Works alongside minDate/maxDate as well.
     */
    dayIsDisabled?: (date: DatePickerDate) => boolean;

    /**
     * If provided, will render the description between the label and the date picker input.
     */
    description?: ReactChild;

    /**
     * Optional props to pass along the descriptive text.
     */
    descriptionProps?: HTMLAttributes<HTMLDivElement> & Pick<TypeRampV2Props, "lineClamp">;

    /**
     * Optional props to pass along to the dialog's usePopper hook.
     */
    dialogPopperProps?: FlyoutProps["popperProps"];

    /**
     * If specified, this is the initial date that will be shown when the
     * user opens the picker dialog. If `value` is set, that date will be used instead.
     *
     * If unset, defaults to the user's locale date.
     */
    initialPickerDate?: DatePickerDate;

    /**
     * Flag to determine if the input is disabled.
     */
    isDisabled?: boolean;

    /**
     * Flag to determine if the input is readOnly.
     */
    isReadOnly?: boolean;

    /**
     * If provided will show an error if field is emptied.
     */
    isRequired?: boolean;

    /**
     * If present, will render the DatePicker within a wrapping label with the given text.
     */
    label?: ReactNode;

    /**
     * The maximum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    maxDate?: DatePickerDate;

    /**
     * The minimum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    minDate?: DatePickerDate;

    /**
     * This function will be called any time the validation state changes internally.
     */
    onValidationStateChange?: (newValidationState: DatePickerInputValidationResult) => void;

    /**
     * Internal component overrides
     */
    overrides?: DatePickerComponentOverrideProps;

    /**
     * If provided, will pass the FlyoutProps to the DatePickerDialog.
     */
    portalProps?: FlyoutProps["portalProps"];

    /**
     * an optional presentational value to be displayed in the input field.
     * This is useful for displaying a different format than the one used
     * to validate or parse the date. See our DatePickerPresentationalFormatStory
     * for an example usage.
     */
    presentationalValue?: string;

    variant?: TextInputProps["variant"];

    /**
     * Determines the width of the DateInput.
     * 'compact' will render the input with a fixed width of 138px.
     * 'responsive' will render the input with a width of 100%.
     * defaults to 'compact'
     */
    width?: "compact" | "responsive";
}

const DatePickerInputBase = forwardRef<HTMLInputElement, DatePickerInputPropsBase & WithI18nInjectedProps>(
    (
        {
            clientId,
            dayIsDisabled,
            format,
            id: providedInputId,
            i18n,
            initialPickerDate,
            label,
            locale,
            maxDate,
            minDate,
            isRequired,
            onValidationStateChange,
            onChange,
            value,
            presentationalValue,
            portalProps,
            width = "compact",
            description,
            descriptionProps,
            "aria-describedby": ariaDescribedBy,
            dialogPopperProps,
            overrides,
            ...rest
        },
        parentInputRef
    ) => {
        const errorMessages = useMemo<ValidationMessages>(() => {
            return {
                isInvalid: i18n._(
                    /*i18n*/
                    {
                        id: "Please enter a valid date ({format})",
                        values: { format },
                        comment:
                            "An error message showing the user has entered a date in the wrong format. It shows the correct format in parenthesis.",
                    }
                ),
                isBefore: i18n._(
                    /*i18n*/ {
                        id: "Please select a date after {minDate}",
                        values: {
                            minDate: dateToInputString(minDate, format),
                        },
                        comment:
                            "An error message showing the user has entered a date before the required minimum date.",
                    }
                ),
                isAfter: i18n._(
                    /*i18n*/ {
                        id: "Please select a date before {maxDate}",
                        values: { maxDate: dateToInputString(maxDate, format) },
                        comment:
                            "An error message showing the user has entered a date after the required maximum date.",
                    }
                ),
                isDisabled: i18n._(
                    /*i18n*/ {
                        id: "The selected date is disabled",
                        comment:
                            "An error message showing the user has entered a date that is not allowed because it is disabled.",
                    }
                ),
                isRequired: i18n._(
                    /*i18n*/ {
                        id: "Please enter a valid date ({format})",
                        values: { format },
                        comment:
                            "An error message telling the user they need to enter a date since it is required. It shows the correct format in parenthesis.",
                    }
                ),
            };
        }, [format, i18n, maxDate, minDate]);
        const inputId = useMemo(() => providedInputId || genId("dp"), [providedInputId]);
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const [errorMessage, setErrorMessage] = useState<string | null>(null);
        const [pickerIsOpen, setPickerIsOpen] = useState(false);
        const calendarButtonRef = useRef<HTMLButtonElement>(null);

        /**
         * @param refocusTarget if true, manually focus the target again. We cannot rely on
         *                      Flyout here since we force the target ref to be the input.
         */
        const closePicker = (refocusTarget: boolean) => {
            setPickerIsOpen(false);
            if (refocusTarget) {
                requestAnimationFrame(() => {
                    calendarButtonRef.current?.focus();
                });
            }
        };

        const openPicker = () => {
            setPickerIsOpen(true);
        };

        const onPickerChange = (val: DatePickerDate | null) => {
            closePicker(true);
            onChange(val);
        };

        const onValidationChange = (validationResult: DatePickerInputValidationResult) => {
            setErrorMessage(validationResult === "isValid" ? null : errorMessages[validationResult]);
            onValidationStateChange?.(validationResult);
        };

        const { getTargetHTMLProps, getPickerProps } = useDatePickerDialog({
            clientId: getClientId(clientId, "dp-dialog"),
            dayIsDisabled: dayIsDisabled,
            initialPickerDate: initialPickerDate,
            isOpen: pickerIsOpen,
            locale,
            maxDate,
            minDate,
            onChange: onPickerChange,
            onClear: () => {
                onPickerChange(null);
            },
            onCloseRequested: closePicker,
            onOpenRequested: openPicker,
            placement: "bottom-end",
            offset: { distance: datePickerTheme.menuOffset(themeMode) },
            portalProps,
            // We'll manage the focus ourselves.
            refocusTargetOnClose: false,
            value,
        });

        const { ref: targetRefFromHook, ...targetProps } = getTargetHTMLProps({
            onClick: openPicker,
        });

        const calendarRef = useForkRef(calendarButtonRef, targetRefFromHook);

        const placeholderId = genId("fl-placeholder");

        return (
            <FormLabel
                label={label as ReactChild}
                id={inputId}
                validationState={errorMessage ? "error" : undefined}
                validationMessage={errorMessage || undefined}
                description={description || undefined}
                descriptionProps={description ? descriptionProps : undefined}
                aria-describedby={ariaDescribedBy}
                placeholderId={placeholderId}
                {...overrides?.formLabel}
            >
                {(labelProps) => (
                    <>
                        <AtomicDatePickerInput
                            {...rest}
                            {...labelProps}
                            calendarButtonProps={{
                                ...targetProps,
                                ref: calendarRef,
                            }}
                            onValidationChange={onValidationChange}
                            clientId={clientId}
                            dayIsDisabled={dayIsDisabled}
                            format={format}
                            maxDate={maxDate}
                            minDate={minDate}
                            isRequired={isRequired}
                            onChange={onChange}
                            ref={parentInputRef}
                            value={value}
                            isCompact={width === "compact"}
                            placeholderId={placeholderId}
                            presentationalValue={presentationalValue}
                            overrides={overrides}
                        />

                        <DatePickerDialog
                            {...getPickerProps()}
                            onCloseRequested={closePicker}
                            popperProps={dialogPopperProps}
                        />
                    </>
                )}
            </FormLabel>
        );
    }
);

DatePickerInputBase.displayName = "DatePickerInputBase";

export type DatePickerInputProps = DatePickerInputPropsBase & I18nProviderPropsStrict;

export const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(({ ...props }, ref) => {
    injectCatalog();
    return (
        <I18nContextProvider locale={props.locale}>
            <I18NContextConsumer>
                {({ i18n }) => <DatePickerInputBase {...props} i18n={i18n} ref={ref} />}
            </I18NContextConsumer>
        </I18nContextProvider>
    );
});

DatePickerInput.displayName = "DatePickerInput";
